import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '../components/loader';
import Header from '../components/header';
import { useNavigate } from 'react-router-dom';
import '../styles/adminhome.css';
import { get } from '../config/api';
import { CHEVERONRIGHT, PCLOGO } from '../constants';
import { API_ENDPOINTS } from '../config/apiEndpoints';




interface Locations {
    code: string;
    name: string;
    level: string;
}

export interface UserInfo {
    id: string;
    name: string;
    type: string;
    primaryPhoneNumber: string;
    address: string;
    postalCode: string;
    status: string;
    registeredDate: string;
    lastAccessedDate: string;
    locations: Locations[];
}

export const AdminHome = () => {
    const navigate = useNavigate();
    const hasFetched = useRef(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userData, setUserData] = useState<UserInfo>();
    const [requests, setRequests] = useState<any[]>([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const listContainerRef = useRef<HTMLDivElement>(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [activeStatus, setActiveStatus] = useState<string>('PENDING_DELIVERY');
    
    // Pending with filter states
    const [showPendingDropdown, setShowPendingDropdown] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [managers, setManagers] = useState<any[]>([]);
    const pendingDropdownRef = useRef<HTMLDivElement>(null);
    const [currentUserId, setCurrentUserId] = useState<string>('');

    const fetchRequests = async (currentPage: number, search: string, status: string = activeStatus, overrideUserId?: string) => {
        if (isLoading) return;
        setIsLoading(true);
        const USER_ID = await localStorage.getItem('USER_ID');
        const storedUserData = localStorage.getItem('userData');
        const userRole = storedUserData ? JSON.parse(storedUserData)?.role || '' : '';
        const parsedUserData = storedUserData ? JSON.parse(storedUserData) : {};
        
        const effectiveUserId = overrideUserId || USER_ID;
        
        try {
            let response: any = {};
            
            // If on Pending Review tab and a specific manager is selected (not @Me)
            if (activeStatus === 'PENDING_APPROVAL' && selectedUserId && selectedUserId !== currentUserId) {
                // Call APPROVED_REQUESTS_BY_ISSUER for a specific manager
                const params = new URLSearchParams({
                    status: status,
                    searchBy: search,
                    page: (currentPage + 1).toString(),
                    size: '10',
                });
                response = await get(`${API_ENDPOINTS.APPROVED_REQUESTS_BY_ISSUER(selectedUserId)}?${params.toString()}`);
            } else {
                // Default call for @Me or other statuses
                response = await get(API_ENDPOINTS.GET_USER_CERTIFICATE_REQUESTS(
                    effectiveUserId,
                    status,
                    search,
                    currentPage + 1,
                    10,
                    userRole,
                ));
            }
            
            if (response && response.items) {
                if (currentPage === 0) {
                    setRequests(response.items);
                } else {
                    setRequests(prev => [...prev, ...response.items]);
                }
                setHasMore(response.items.length > 0 && (currentPage + 1) * 10 < response.total);
                setPage(currentPage + 1);
            } else {
                if (currentPage === 0) {
                    setRequests([]);
                }
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error fetching requests:', error);
            setRequests([]);
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleScroll = () => {
        const container = listContainerRef.current;
        if (container) {
            const { scrollTop, scrollHeight, clientHeight } = container;
            // Load more when the user is 50px from the bottom
            if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isLoading) {
                fetchRequests(page, debouncedSearchTerm, activeStatus);
            }
        }
    };


    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const userid = searchParams.get('userId');
        const userstate = searchParams.get('state');

        if (!userid || !userstate) {
             navigate('/error');
            return;
        }
        if (userid) {
            localStorage.setItem('USER_ID', userid);
        }
        if (userstate) {
            localStorage.setItem('USER_STATE', userstate);
        }
        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchUserData();
        }
    }, []);

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (pendingDropdownRef.current && !pendingDropdownRef.current.contains(e.target as Node)) {
                setShowPendingDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // 500ms debounce delay

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        // This effect triggers the API call when the debounced search term changes,
        // active status changes, or on initial load.
        setPage(0);
        setRequests([]);
        fetchRequests(0, debouncedSearchTerm, activeStatus);
    }, [debouncedSearchTerm, activeStatus, selectedUserId]);

    useEffect(() => {
        const container = listContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [isLoading, hasMore, page]);

    const handleSearch = (e: any) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
    };
    
    const fetchUserData = async () => {
        try {
            let user: any = await localStorage.getItem('userData');
            const parsedUser = JSON.parse(user);
            setUserData(parsedUser);
            setCurrentUserId(parsedUser?.user_id || '');
            setSelectedUserId(parsedUser?.user_id || '');
            // Fetch managers after setting user data
            if (parsedUser?.user_id) {
                fetchManagers(parsedUser.user_id);
            }
        } catch (error) {
            console.log(error)
        }
    };

    const fetchManagers = async (userId: string) => {
        if (!userId) return;
        try {
            const response = await get(API_ENDPOINTS.USER_MANAGERS(userId));
            if (response && Array.isArray(response)) {
                setManagers(response);
            }
        } catch (error) {
            console.error('Failed to fetch managers:', error);
            setManagers([]);
        }
    };

    const handlePendingWithSelect = (id: string) => {
        setSelectedUserId(id);
        setPage(0);
        setRequests([]);
        setShowPendingDropdown(false);
    };

    const getSelectedLabel = () => {
        if (selectedUserId === currentUserId) return '@Me';
        const found = managers.find((m: any) => m.user_id === selectedUserId);
        return found ? found.username : '@Me';
    };

    const handleHelpClick = () => {
        navigate('/help');
    };

    const handleCardClick = (req: any) => {
        navigate('/summary', { state: { request: req } });
    };

    const handleIssueCertificateClick = () => {
        navigate('/request-certificate', { state: { request: { ...userData, fromHome: false } } });
    }
    return (
        <div className="page-container">
            {isLoading && <Loader />}
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                borderBottom: '1px solid #efefef',
                padding: '15px 20px', marginBottom: 20,
                position: 'fixed', left: 0, right: 0, top: 0, background: '#fff', zIndex: 9
            }}>
                <img src={PCLOGO} height={30} style={{ marginRight: 20 }} alt="" />
                <span style={{ display: 'block', marginLeft: 12, width: 2, background: 'rgba(0, 0, 0, 0.60)', height: 28 }}></span>
                <span style={{
                    display: 'block',
                    width: 190,
                    paddingLeft: 12,
                    fontSize:14,
                    color: 'rgba(0, 0, 0, 0.60)',

                }}>Principal Certificate Management System</span>
            </div>
            <div style={{ paddingTop: 70 }}>
                <div className="status-buttons">
                    <button
                        className={`status-button ${activeStatus === 'PENDING_DELIVERY' ? 'active' : ''}`}
                        onClick={() => setActiveStatus('PENDING_DELIVERY')}
                    >Pending Delivery</button>
                    <button
                        className={`status-button ${activeStatus === 'PENDING_APPROVAL' ? 'active' : ''}`}
                        onClick={() => setActiveStatus('PENDING_APPROVAL')}
                    >Pending Review</button>
                </div>

                <div className="search-bar">
                    <input
                        type="text"
                        id="searchInput"
                        className="search-input"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={handleSearch}
                        disabled={isLoading && requests.length === 0}
                    />
                </div>

                {activeStatus === 'PENDING_APPROVAL' && managers.length > 0 && (
                    <div className="pending-with-wrapper" ref={pendingDropdownRef} style={{ padding: '10px 20px' }}>
                        <span style={{ fontSize: 13, fontWeight: 500, marginRight: 6, whiteSpace: 'nowrap' }}>Pending with:</span>
                        <div className="pending-with-trigger" onClick={() => setShowPendingDropdown(!showPendingDropdown)}>
                            <span>{getSelectedLabel()}</span>
                            <span className={`pending-with-arrow ${showPendingDropdown ? 'open' : ''}`}>&#9660;</span>
                        </div>
                        {showPendingDropdown && (
                            <div className="pending-with-dropdown">
                                <div 
                                    className={`pending-with-option ${selectedUserId === currentUserId ? 'selected' : ''}`}
                                    onClick={() => handlePendingWithSelect(currentUserId)}
                                >
                                    @Me
                                    {selectedUserId === currentUserId && <span className="pending-with-check">&#10003;</span>}
                                </div>
                                {managers.map((manager: any) => (
                                    <div 
                                        key={manager.user_id}
                                        className={`pending-with-option ${selectedUserId === manager.user_id ? 'selected' : ''}`}
                                        onClick={() => handlePendingWithSelect(manager.user_id)}
                                    >
                                        {manager.username}
                                        {selectedUserId === manager.user_id && <span className="pending-with-check">&#10003;</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="list-header">
                    <div className="firm-name-header">Firm Name</div>
                    <div className="action-header">Action</div>
                </div>

                <div className="list-container" ref={listContainerRef}>
                    {isLoading && requests.length === 0 ? (
                        <div className="loading">Loading...</div>
                    ) : requests.length > 0 ? (
                        requests.map((item: any, index) => (
                            <div className="list-item" key={index} onClick={() => { handleCardClick(item); }}>
                                <div className="firm-name">{item.firm_name} 
                                    <span className="brp-id">BRP ID: {item.partner_id}</span>
                                </div>
                                <div className="action-icon">
                                    <img src={CHEVERONRIGHT} height={25} alt="action" />
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-results">No pending delivery requests found.</div>
                    )}
                    {isLoading && requests.length > 0 && <div className="loading">Loading more...</div>}
                </div>

            </div>
        </div>
    )
}